import { useState } from 'react';
import { Layout } from '../../components/Layout';
import { get, post, del, uploadImage } from '../../api/superAdminClient';
import useSWR from 'swr';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import toast from 'react-hot-toast';
import { convertToWebP } from '@pos/shared/utils/image-upload';

/** 單一 bubble 的內容（輪播時每頁可不同）；與 API bubbles 形狀相容 */
interface DesignBubbleItem {
  title: string;
  body: string;
  buttonLabel: string;
  buttonUrl: string;
  hero: {
    url: string;
    aspectRatio?: string;
    aspectMode?: string;
    actionUrl?: string;
  } | null;
}

function defaultBubble(): DesignBubbleItem {
  return {
    title: '',
    body: '',
    buttonLabel: '',
    buttonUrl: '',
    hero: null,
  };
}

/** 已儲存項目（API 回傳；bubbles 為新格式，無 bubbles 時 applyPreset 用頂層 title/body/hero 相容） */
interface SavedPreset {
  id: string;
  name: string;
  scenario: string;
  bubbleCount: number;
  bubbleSize: string;
  altText: string;
  bubbles?: DesignBubbleItem[];
  hero?: {
    url: string;
    aspectRatio?: string;
    aspectMode?: string;
    actionUrl?: string;
  } | null;
  title?: string;
  body?: string;
  buttonLabel?: string;
  buttonUrl?: string;
  createdAt?: string;
  updatedAt?: string;
}

/**
 * LITE 群組列表（用於多選）
 */
interface LiteGroupListResponse {
  success: boolean;
  data: {
    groups: Array<{ groupId: string; name: string; isActive: boolean }>;
    total: number;
  };
}

/**
 * 發送結果
 */
interface SendResult {
  sent: string[];
  failed: Array<{ groupId: string; error: string }>;
}

/**
 * LITE 廣告頁
 * 針對不同 LITE 群組發送客製 Flex Message（與「發布活動」「/bot」webhook 並行）
 */
/** Bubble 大小選項 */
const BUBBLE_SIZES = [
  { v: 'nano', l: 'nano (約 25%)' },
  { v: 'micro', l: 'micro (約 33%)' },
  { v: 'kilo', l: 'kilo (約 50%)' },
  { v: 'mega', l: 'mega (約 75%)' },
  { v: 'giga', l: 'giga (100%)' },
  { v: 'full', l: 'full (全寬)' },
] as const;

/** Hero 比例選項 */
const HERO_ASPECT_RATIOS = [
  '20:13',
  '1:1',
  '1.51:1',
  '1.91:1',
  '4:3',
  '16:9',
  '2:1',
  '3:1',
  '3:4',
  '9:16',
  '1:2',
  '1:3',
] as const;

/** Bubble 情境（對應自動發送 Flex 的兩種情況） */
const SCENARIO_OPTIONS = [
  { v: 'custom', l: '自訂' },
  { v: 'publish', l: '發布活動版型' },
  { v: 'assistant', l: '啟動助理版型' },
] as const;

function getScenarioLabel(s: string): string {
  return SCENARIO_OPTIONS.find(o => o.v === s)?.l ?? s;
}

export function LiteAds() {
  // Bubble 設計（designBubbles.length === designBubbleCount；數量>1 時用 tab 切換 activeBubbleIndex）
  const [designScenario, setDesignScenario] = useState<
    'custom' | 'publish' | 'assistant'
  >('custom');
  const [designBubbleCount, setDesignBubbleCount] = useState(1);
  const [designBubbleSize, setDesignBubbleSize] = useState<string>('kilo');
  const [designBubbles, setDesignBubbles] = useState<DesignBubbleItem[]>(() => [
    defaultBubble(),
  ]);
  const [activeBubbleIndex, setActiveBubbleIndex] = useState(0);
  const [designAltText, setDesignAltText] = useState('');
  const [heroUploading, setHeroUploading] = useState(false);

  // 已儲存（API / DB）
  type PresetsListRes = {
    success: boolean;
    data: {
      items: SavedPreset[];
      total: number;
      page: number;
      limit: number;
      totalPages: number;
    };
  };
  const { data: presetsRes, mutate: mutatePresets } = useSWR<PresetsListRes>(
    '/api/super-admin/lite/ads/presets',
    (url: string) => get<PresetsListRes>(url)
  );
  const savedPresets = presetsRes?.data?.items ?? [];

  const applyPreset = (p: SavedPreset) => {
    setDesignScenario(
      p.scenario === 'custom' ||
        p.scenario === 'publish' ||
        p.scenario === 'assistant'
        ? p.scenario
        : 'custom'
    );
    setDesignBubbleSize(p.bubbleSize);
    setDesignAltText(p.altText);
    const n = Math.max(1, p.bubbleCount || 1);
    setDesignBubbleCount(n);
    if (p.bubbles && Array.isArray(p.bubbles) && p.bubbles.length > 0) {
      const arr = [...p.bubbles];
      while (arr.length < n)
        arr.push({ ...(arr[arr.length - 1] ?? defaultBubble()) });
      setDesignBubbles(arr.slice(0, n));
    } else {
      const b: DesignBubbleItem = {
        title: p.title ?? '',
        body: p.body ?? '',
        buttonLabel: p.buttonLabel ?? '',
        buttonUrl: p.buttonUrl ?? '',
        hero: p.hero ?? null,
      };
      setDesignBubbles(Array.from({ length: n }, () => ({ ...b })));
    }
    setActiveBubbleIndex(0);
    toast.success('已載入');
  };

  const handleSaveCurrent = async () => {
    const invalid = designBubbles.some(b => !b.title.trim() || !b.body.trim());
    if (invalid || !designAltText.trim()) {
      toast.error('請填寫每一頁的標題、內文與 altText 後再儲存');
      return;
    }
    try {
      await post('/api/super-admin/lite/ads/presets/upsert', {
        scenario: designScenario,
        bubbleCount: designBubbleCount,
        bubbleSize: designBubbleSize,
        altText: designAltText,
        bubbles: designBubbles.map(b => ({
          title: b.title,
          body: b.body,
          buttonLabel: b.buttonLabel || undefined,
          buttonUrl: b.buttonUrl || undefined,
          hero: b.hero?.url
            ? {
                url: b.hero.url,
                aspectRatio: b.hero.aspectRatio,
                aspectMode: b.hero.aspectMode,
                ...(b.hero.actionUrl?.trim()
                  ? { actionUrl: b.hero.actionUrl }
                  : {}),
              }
            : undefined,
        })),
      });
      await mutatePresets();
      toast.success('已儲存（同情境已覆蓋）');
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : '儲存失敗');
    }
  };

  const updateBubble = (i: number, patch: Partial<DesignBubbleItem>) => {
    setDesignBubbles(prev => {
      const n = [...prev];
      n[i] = { ...n[i]!, ...patch };
      return n;
    });
  };

  const handleHeroFile = async (file: File) => {
    if (!file.type.startsWith('image/')) {
      toast.error('請上傳圖片檔案');
      return;
    }
    const i = activeBubbleIndex;
    setHeroUploading(true);
    try {
      const webpFile = await convertToWebP(file);
      const { url } = await uploadImage(
        '/api/super-admin/lite/ads/upload-image',
        webpFile
      );
      setDesignBubbles(prev => {
        const n = [...prev];
        const cur = n[i] ?? defaultBubble();
        const h = cur.hero ?? {
          url: '',
          aspectRatio: '20:13',
          aspectMode: 'cover',
          actionUrl: '',
        };
        n[i] = { ...cur, hero: { ...h, url } };
        return n;
      });
      toast.success('已上傳');
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : '上傳失敗');
    } finally {
      setHeroUploading(false);
    }
  };

  const handleBubbleCountChange = (v: number) => {
    const n = Math.max(1, Math.min(10, v || 1));
    setDesignBubbleCount(n);
    setDesignBubbles(prev => {
      if (n > prev.length) {
        const last = prev[prev.length - 1] ?? defaultBubble();
        return [
          ...prev,
          ...Array.from({ length: n - prev.length }, () => ({ ...last })),
        ];
      }
      return prev.slice(0, n);
    });
    setActiveBubbleIndex(a => (a >= n ? Math.max(0, n - 1) : a));
  };

  const [target, setTarget] = useState<'all' | 'select'>('all');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  const [sending, setSending] = useState(false);
  const [lastResult, setLastResult] = useState<SendResult | null>(null);

  const {
    data: groupsData,
    error: groupsError,
    isLoading: groupsLoading,
  } = useSWR<LiteGroupListResponse>(
    '/api/super-admin/lite/groups?isActive=true&limit=100&page=1',
    get as (u: string) => Promise<LiteGroupListResponse>
  );

  const groups = groupsData?.data?.groups ?? [];

  const toggleGroup = (groupId: string) => {
    setSelectedIds(prev => {
      const n = new Set(prev);
      if (n.has(groupId)) n.delete(groupId);
      else n.add(groupId);
      return n;
    });
  };

  const handleSend = async () => {
    const invalid = designBubbles.some(b => !b.title.trim() || !b.body.trim());
    if (invalid || !designAltText.trim()) {
      toast.error('請填寫每一頁的標題、內文與 altText');
      return;
    }
    const payload: { bubbleDesign: object; groupIds?: string[] } = {
      bubbleDesign: {
        bubbleCount: Math.max(1, Math.min(10, designBubbleCount)),
        bubbleSize: designBubbleSize || 'kilo',
        altText: designAltText.trim(),
        scenario: designScenario,
        bubbles: designBubbles.map((b, i) => {
          const isFirstWithProgrammatic =
            (designScenario === 'publish' || designScenario === 'assistant') &&
            i === 0;
          return {
            title: b.title.trim(),
            body: b.body.trim(),
            ...(b.buttonLabel.trim()
              ? {
                  buttonLabel: b.buttonLabel.trim(),
                  buttonUrl: isFirstWithProgrammatic
                    ? '__PROGRAMMATIC__'
                    : b.buttonUrl.trim() || undefined,
                }
              : {}),
            ...(b.hero?.url
              ? {
                  hero: {
                    url: b.hero.url,
                    aspectRatio: b.hero.aspectRatio || '20:13',
                    aspectMode: b.hero.aspectMode || 'cover',
                    ...(b.hero.actionUrl?.trim()
                      ? { actionUrl: b.hero.actionUrl.trim() }
                      : {}),
                  },
                }
              : {}),
          };
        }),
      },
    };

    if (target === 'select' && selectedIds.size === 0) {
      toast.error('請至少選擇一個群組，或改選「全部啟用群組」');
      return;
    }
    if (target === 'select') payload.groupIds = Array.from(selectedIds);

    setSending(true);
    setLastResult(null);
    try {
      const res = await post<{ success: boolean; data: SendResult }>(
        '/api/super-admin/lite/ads/send',
        payload
      );
      setLastResult(res.data);
      if (res.data.failed.length === 0) {
        toast.success(`已發送至 ${res.data.sent.length} 個群組`);
      } else {
        toast.success(
          `已發送 ${res.data.sent.length} 個，${res.data.failed.length} 個失敗`
        );
      }
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : '發送失敗';
      toast.error(msg);
    } finally {
      setSending(false);
    }
  };

  const handleScenarioChange = (v: 'custom' | 'publish' | 'assistant') => {
    setDesignScenario(v);
    if (v === 'publish') {
      const b: DesignBubbleItem = {
        title: '活動已啟動',
        body: '請輸入活動名稱',
        buttonLabel: '立即參與',
        buttonUrl: '',
        hero: null,
      };
      setDesignBubbleCount(2);
      setDesignBubbleSize('kilo');
      setDesignBubbles([{ ...b }, { ...b }]);
      setDesignAltText('活動已啟動');
      setActiveBubbleIndex(0);
    } else if (v === 'assistant') {
      const b: DesignBubbleItem = {
        title: '啟動商家助理',
        body: '點擊下方按鈕開始設定您的商家助理',
        buttonLabel: '啟動我的助理',
        buttonUrl: '',
        hero: null,
      };
      setDesignBubbleCount(2);
      setDesignBubbleSize('kilo');
      setDesignBubbles([{ ...b }, { ...b }]);
      setDesignAltText('啟動商家助理');
      setActiveBubbleIndex(0);
    }
  };

  return (
    <Layout title="LITE 廣告">
      <div className="flex gap-6">
        <div className="flex-1 min-w-0 max-w-2xl space-y-6">
          <div className="bg-white rounded-lg shadow p-4 space-y-6">
            {/* 情境（對應自動發送 Flex 的版型） */}
            <fieldset className="space-y-3">
              <legend className="text-sm font-medium text-gray-700">
                Bubble 情境
              </legend>
              <select
                value={designScenario}
                onChange={e =>
                  handleScenarioChange(
                    e.target.value as 'custom' | 'publish' | 'assistant'
                  )
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              >
                {SCENARIO_OPTIONS.map(o => (
                  <option key={o.v} value={o.v}>
                    {o.l}
                  </option>
                ))}
              </select>
              <p className="text-xs text-gray-500">
                發布活動版型、啟動助理版型對應系統自動發送 Flex
                的情境，可載入後再修改。
              </p>
            </fieldset>

            {/* 區塊一：Bubble 數量與大小 */}
            <fieldset className="space-y-3">
              <legend className="text-sm font-medium text-gray-700">
                Bubble 數量與大小
              </legend>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-600 mb-1">
                    Bubble 數量
                  </label>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() =>
                        handleBubbleCountChange(designBubbleCount - 1)
                      }
                      disabled={designBubbleCount <= 1}
                      className="h-10 w-10 shrink-0 rounded-lg border border-gray-300 bg-white text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-white flex items-center justify-center text-lg font-medium"
                      aria-label="減少"
                    >
                      −
                    </button>
                    <span className="min-w-[2rem] text-center font-medium tabular-nums">
                      {designBubbleCount}
                    </span>
                    <button
                      type="button"
                      onClick={() =>
                        handleBubbleCountChange(designBubbleCount + 1)
                      }
                      disabled={designBubbleCount >= 10}
                      className="h-10 w-10 shrink-0 rounded-lg border border-gray-300 bg-white text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-white flex items-center justify-center text-lg font-medium"
                      aria-label="增加"
                    >
                      +
                    </button>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    1＝單一；2–10＝輪播張數，可切換分頁設不同內容
                  </p>
                </div>
                <div>
                  <label className="block text-sm text-gray-600 mb-1">
                    Bubble 大小
                  </label>
                  <select
                    value={designBubbleSize}
                    onChange={e => setDesignBubbleSize(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  >
                    {BUBBLE_SIZES.map(s => (
                      <option key={s.v} value={s.v}>
                        {s.l}
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    輪播時不支援，僅數量＝1 有效
                  </p>
                </div>
              </div>
            </fieldset>

            {/* 分頁 Tab：數量>1 時切換每一頁的 hero、內文 */}
            {designBubbleCount > 1 && (
              <div className="flex gap-1 border-b border-gray-200 pb-2">
                {Array.from({ length: designBubbleCount }, (_, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => setActiveBubbleIndex(i)}
                    className={`px-3 py-1.5 text-sm rounded-t ${activeBubbleIndex === i ? 'bg-primary text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                  >
                    第 {i + 1} 頁
                  </button>
                ))}
              </div>
            )}

            {/* 區塊二：上方圖片 (hero) — 依分頁，支援拖曳、貼上、預覽 */}
            <fieldset className="space-y-3">
              <legend className="text-sm font-medium text-gray-700">
                上方圖片 (hero)
                {designBubbleCount > 1
                  ? ` — 第 ${activeBubbleIndex + 1} 頁`
                  : ''}
              </legend>
              <p className="text-xs text-gray-500">
                選填。可拖曳或貼上圖片上傳，或直接輸入網址。比例、模式與點擊連結可調。
              </p>
              {(() => {
                const cur = designBubbles[activeBubbleIndex] ?? defaultBubble();
                const heroUrl = cur.hero?.url ?? '';
                return (
                  <>
                    <div
                      className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center min-h-[120px] flex flex-col items-center justify-center gap-2"
                      onDragOver={e => {
                        e.preventDefault();
                        e.stopPropagation();
                      }}
                      onDrop={e => {
                        e.preventDefault();
                        e.stopPropagation();
                        const f = e.dataTransfer.files?.[0];
                        if (f) handleHeroFile(f);
                      }}
                      onPaste={e => {
                        const f = e.clipboardData?.files?.[0];
                        if (f) {
                          e.preventDefault();
                          handleHeroFile(f);
                        }
                      }}
                    >
                      {heroUploading ? (
                        <LoadingSpinner size="sm" />
                      ) : heroUrl ? (
                        <div className="relative w-full max-h-40">
                          <img
                            src={heroUrl}
                            alt="Hero 預覽"
                            className="max-h-40 w-full object-contain rounded bg-gray-100"
                            onError={e => {
                              (e.target as HTMLImageElement).style.display =
                                'none';
                            }}
                          />
                          <button
                            type="button"
                            onClick={() =>
                              updateBubble(activeBubbleIndex, { hero: null })
                            }
                            className="absolute top-1 right-1 text-xs bg-black/50 text-white px-2 py-1 rounded"
                          >
                            移除
                          </button>
                        </div>
                      ) : (
                        <span className="text-sm text-gray-500">
                          拖曳圖片到這裡，或貼上 (Ctrl+V)
                        </span>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm text-gray-600 mb-1">
                        或輸入圖片 URL
                      </label>
                      <input
                        type="url"
                        value={heroUrl}
                        onChange={e => {
                          const v = e.target.value;
                          if (!v.trim())
                            updateBubble(activeBubbleIndex, { hero: null });
                          else {
                            const h = cur.hero ?? {
                              url: '',
                              aspectRatio: '20:13',
                              aspectMode: 'cover',
                              actionUrl: '',
                            };
                            updateBubble(activeBubbleIndex, {
                              hero: { ...h, url: v },
                            });
                          }
                        }}
                        placeholder="https://..."
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm text-gray-600 mb-1">
                          比例 (aspectRatio)
                        </label>
                        <select
                          value={cur.hero?.aspectRatio ?? '20:13'}
                          onChange={e => {
                            const h = cur.hero ?? {
                              url: '',
                              aspectRatio: '20:13',
                              aspectMode: 'cover',
                              actionUrl: '',
                            };
                            updateBubble(activeBubbleIndex, {
                              hero: { ...h, aspectRatio: e.target.value },
                            });
                          }}
                          disabled={!cur.hero}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent disabled:opacity-50"
                        >
                          {HERO_ASPECT_RATIOS.map(r => (
                            <option key={r} value={r}>
                              {r}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm text-gray-600 mb-1">
                          模式 (aspectMode)
                        </label>
                        <select
                          value={cur.hero?.aspectMode ?? 'cover'}
                          onChange={e => {
                            const h = cur.hero ?? {
                              url: '',
                              aspectRatio: '20:13',
                              aspectMode: 'cover',
                              actionUrl: '',
                            };
                            updateBubble(activeBubbleIndex, {
                              hero: {
                                ...h,
                                aspectMode: e.target.value as 'cover' | 'fit',
                              },
                            });
                          }}
                          disabled={!cur.hero}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent disabled:opacity-50"
                        >
                          <option value="cover">cover（填滿，可能裁切）</option>
                          <option value="fit">fit（完整顯示，可能留白）</option>
                        </select>
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm text-gray-600 mb-1">
                        點擊連結（選填）
                      </label>
                      <input
                        type="url"
                        value={cur.hero?.actionUrl ?? ''}
                        onChange={e => {
                          const h = cur.hero ?? {
                            url: '',
                            aspectRatio: '20:13',
                            aspectMode: 'cover',
                            actionUrl: '',
                          };
                          updateBubble(activeBubbleIndex, {
                            hero: { ...h, actionUrl: e.target.value },
                          });
                        }}
                        placeholder="https://...（點圖開啟）"
                        disabled={!cur.hero}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent disabled:opacity-50"
                      />
                    </div>
                  </>
                );
              })()}
            </fieldset>

            {/* 區塊三：內文（依分頁） */}
            <fieldset className="space-y-3">
              <legend className="text-sm font-medium text-gray-700">
                內文
                {designBubbleCount > 1
                  ? ` — 第 ${activeBubbleIndex + 1} 頁`
                  : ''}
              </legend>
              {(() => {
                const cur = designBubbles[activeBubbleIndex] ?? defaultBubble();
                return (
                  <>
                    <div>
                      <label className="block text-sm text-gray-600 mb-1">
                        標題 *
                      </label>
                      <input
                        type="text"
                        value={cur.title}
                        onChange={e =>
                          updateBubble(activeBubbleIndex, {
                            title: e.target.value,
                          })
                        }
                        placeholder="例：最新優惠"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-gray-600 mb-1">
                        內文 *
                      </label>
                      <textarea
                        value={cur.body}
                        onChange={e =>
                          updateBubble(activeBubbleIndex, {
                            body: e.target.value,
                          })
                        }
                        placeholder="例：即日起至本週日止..."
                        rows={3}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm text-gray-600 mb-1">
                          按鈕文字（選填）
                        </label>
                        <input
                          type="text"
                          value={cur.buttonLabel}
                          onChange={e =>
                            updateBubble(activeBubbleIndex, {
                              buttonLabel: e.target.value,
                            })
                          }
                          placeholder="例：立即查看"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                        />
                      </div>
                      <div>
                        <label className="block text-sm text-gray-600 mb-1">
                          按鈕連結（選填）
                        </label>
                        {(designScenario === 'publish' ||
                          designScenario === 'assistant') &&
                        activeBubbleIndex === 0 ? (
                          <>
                            <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2 mb-1">
                              此情境第一頁的按鈕連結由系統依群組、LIFF
                              等資訊自動產生，無法修改。
                            </p>
                            <input
                              type="text"
                              readOnly
                              value="(由系統產生)"
                              className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 text-gray-500 cursor-not-allowed"
                            />
                          </>
                        ) : (
                          <input
                            type="url"
                            value={cur.buttonUrl}
                            onChange={e =>
                              updateBubble(activeBubbleIndex, {
                                buttonUrl: e.target.value,
                              })
                            }
                            placeholder="https://..."
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                          />
                        )}
                      </div>
                    </div>
                  </>
                );
              })()}
            </fieldset>

            {/* 區塊四：altText */}
            <fieldset className="space-y-3">
              <legend className="text-sm font-medium text-gray-700">
                推播摘要 (altText) *
              </legend>
              <input
                type="text"
                value={designAltText}
                onChange={e => setDesignAltText(e.target.value)}
                placeholder="例：最新優惠來了"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              />
            </fieldset>
            <div>
              <button
                type="button"
                onClick={handleSaveCurrent}
                className="px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                儲存目前設定
              </button>
            </div>
          </div>

          {/* 發送對象 */}
          <div className="bg-white rounded-lg shadow p-4">
            <h3 className="text-sm font-medium text-gray-700 mb-3">發送對象</h3>
            <div className="flex flex-col gap-3">
              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  name="target"
                  checked={target === 'all'}
                  onChange={() => setTarget('all')}
                  className="text-primary"
                />
                全部啟用群組
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  name="target"
                  checked={target === 'select'}
                  onChange={() => setTarget('select')}
                  className="text-primary"
                />
                選擇群組
              </label>
              {target === 'select' && (
                <div className="mt-2 pl-6 max-h-48 overflow-y-auto border border-gray-200 rounded-lg p-3">
                  {groupsLoading ? (
                    <LoadingSpinner size="sm" />
                  ) : groupsError ? (
                    <span className="text-red-600">無法載入群組</span>
                  ) : groups.length === 0 ? (
                    <span className="text-gray-500">目前無啟用群組</span>
                  ) : (
                    <div className="space-y-1">
                      {groups.map(g => (
                        <label
                          key={g.groupId}
                          className="flex items-center gap-2 cursor-pointer"
                        >
                          <input
                            type="checkbox"
                            checked={selectedIds.has(g.groupId)}
                            onChange={() => toggleGroup(g.groupId)}
                            className="text-primary rounded"
                          />
                          <span className="text-sm">{g.name || g.groupId}</span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="flex items-center gap-4">
            <button
              onClick={handleSend}
              disabled={sending}
              className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {sending ? '發送中…' : '發送'}
            </button>
          </div>

          {lastResult && (
            <div className="bg-white rounded-lg shadow p-4">
              <h3 className="text-sm font-medium text-gray-700 mb-2">
                發送結果
              </h3>
              <p className="text-sm text-gray-600">
                成功：{lastResult.sent.length} 個群組
              </p>
              {lastResult.failed.length > 0 && (
                <div className="mt-2">
                  <p className="text-sm text-red-600">
                    失敗：{lastResult.failed.length} 個
                  </p>
                  <ul className="mt-1 text-xs text-gray-500 list-disc list-inside">
                    {lastResult.failed.slice(0, 5).map(f => (
                      <li key={f.groupId}>
                        {f.groupId.slice(0, 8)}...：{f.error}
                      </li>
                    ))}
                    {lastResult.failed.length > 5 && (
                      <li>…其餘 {lastResult.failed.length - 5} 筆</li>
                    )}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>

        {/* 右側：已儲存 + Flex 預覽（浮動，捲動主內容時不被遮掉） */}
        <aside className="w-72 flex-shrink-0 self-start sticky top-4 flex flex-col gap-4 max-h-[calc(100vh-2rem)] overflow-y-auto">
          <div className="bg-white rounded-lg shadow p-4">
            <h3 className="text-sm font-medium text-gray-700 mb-3">已儲存</h3>
            <p className="text-xs text-gray-500 mb-3">點選載入後可修改參數</p>
            <ul className="space-y-2 max-h-[280px] overflow-y-auto">
              {savedPresets.length === 0 ? (
                <li className="text-sm text-gray-500">尚無儲存的設定</li>
              ) : (
                savedPresets.map(p => (
                  <li key={p.id} className="border border-gray-200 rounded p-2">
                    <div className="font-medium text-sm truncate">{p.name}</div>
                    <div className="text-xs text-gray-500">
                      {getScenarioLabel(p.scenario)}
                    </div>
                    <div className="mt-2 flex gap-2">
                      <button
                        type="button"
                        onClick={() => applyPreset(p)}
                        className="text-xs text-primary hover:underline"
                      >
                        載入
                      </button>
                      <button
                        type="button"
                        onClick={async () => {
                          try {
                            await del(
                              `/api/super-admin/lite/ads/presets/${p.id}`
                            );
                            await mutatePresets();
                            toast.success('已刪除');
                          } catch (e: unknown) {
                            toast.error(
                              e instanceof Error ? e.message : '刪除失敗'
                            );
                          }
                        }}
                        className="text-xs text-red-600 hover:underline"
                      >
                        刪除
                      </button>
                    </div>
                  </li>
                ))
              )}
            </ul>
          </div>
          {/* Flex 預覽：只顯示當下那一頁 */}
          <div className="bg-white rounded-lg shadow p-4">
            <h3 className="text-sm font-medium text-gray-700 mb-2">
              Flex 預覽
            </h3>
            <p className="text-xs text-gray-500 mb-3">
              推播：{designAltText || '—'}
            </p>
            {designBubbleCount > 1 && (
              <p className="text-xs text-gray-400 mb-2">
                第 {activeBubbleIndex + 1} 頁 / 共 {designBubbleCount} 頁
              </p>
            )}
            <div className="flex flex-col items-center max-h-[420px] overflow-y-auto">
              {(() => {
                const b = designBubbles[activeBubbleIndex] ?? defaultBubble();
                return (
                  <div className="w-full max-w-[240px] rounded-xl border border-gray-200 overflow-hidden bg-white shadow-sm">
                    {b.hero?.url ? (
                      <div className="w-full aspect-[20/13] bg-gray-100 overflow-hidden">
                        <img
                          src={b.hero.url}
                          alt=""
                          className={`w-full h-full ${b.hero?.aspectMode === 'fit' ? 'object-contain' : 'object-cover'}`}
                          onError={e => {
                            (e.target as HTMLImageElement).style.display =
                              'none';
                          }}
                        />
                      </div>
                    ) : null}
                    <div className="p-3 space-y-2">
                      <div className="font-bold text-gray-800 text-sm leading-tight">
                        {b.title || '（標題）'}
                      </div>
                      <div className="text-xs text-gray-600 whitespace-pre-wrap break-words">
                        {b.body || '（內文）'}
                      </div>
                      {b.buttonLabel ? (
                        <div className="pt-1">
                          <span className="inline-block px-3 py-1.5 text-xs font-medium rounded-full bg-primary/15 text-primary">
                            {b.buttonLabel}
                            {(designScenario === 'publish' ||
                              designScenario === 'assistant') &&
                              activeBubbleIndex === 0 && (
                                <span className="text-[10px] text-gray-500 ml-1">
                                  (系統產生連結)
                                </span>
                              )}
                          </span>
                        </div>
                      ) : null}
                    </div>
                  </div>
                );
              })()}
            </div>
          </div>
        </aside>
      </div>
    </Layout>
  );
}
